/**
 * The problem: The Froala editor loses cursor focus when pressing Backspace
 * in this situation:
 * <referenceSpan></p><p><br>|</p> (|: is the location of the caret)
 * because Froala fails to merge the paragraphs in this case.
 */

/**
 * Returns true only when ALL of the following are true:
 *  - The selection is collapsed (caret, not a range selection)
 *  - The caret is inside an empty paragraph (contains only a <br>)
 *  - The previous sibling paragraph's last child element has a data-reference-id attribute
 *
 * This is the exact scenario where pressing Backspace causes Froala to lose cursor
 * focus when a contenteditable="false" reference span is the last element of the
 * preceding paragraph.
 */
export function isBackspaceAfterReferenceParagraph(selection: Selection | null): boolean {
  if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) return false;

  const range = selection.getRangeAt(0);
  const container = range.startContainer;

  const currentP = getParagraph(container);
  if (!currentP) return false;

  const isEmptyParagraph = currentP.childNodes.length === 1 && currentP.firstChild?.nodeName === 'BR';
  if (!isEmptyParagraph) return false;

  const previousP = currentP.previousElementSibling;
  if (!previousP) return false;

  return !!previousP.lastElementChild?.hasAttribute('data-reference-id');
}

/**
 * Performs the paragraph merge when isBackspaceAfterReferenceParagraph() returned true:
 *  - Ensures a text node exists after the reference span in the previous paragraph
 *    (so the browser has a valid cursor anchor)
 *  - Places the cursor in that text node
 *  - Removes the now-empty paragraph
 *
 * Must only be called after confirming isBackspaceAfterReferenceParagraph() is true.
 */
export function mergeEmptyParagraphAfterReference(selection: Selection): void {
  const range = selection.getRangeAt(0);
  const container = range.startContainer;

  const currentP = getParagraph(container) as HTMLElement;
  const prevP = currentP.previousElementSibling as HTMLElement;

  let anchor: Text;
  const lastChild = prevP.lastChild;
  if (lastChild && lastChild.nodeType === Node.TEXT_NODE) {
    anchor = lastChild as Text;
  } else {
    anchor = document.createTextNode('\u200B');
    prevP.appendChild(anchor);
  }

  const newRange = document.createRange();
  newRange.setStart(anchor, anchor.length);
  newRange.collapse(true);
  selection.removeAllRanges();
  selection.addRange(newRange);

  currentP.remove();
}

function getParagraph(node: Node): HTMLElement | null {
  if (node.nodeType === Node.ELEMENT_NODE) {
    const el = node as HTMLElement;
    return el.tagName === 'P' ? el : el.closest('p');
  }
  return (node as Text).parentElement?.closest('p') ?? null;
}
